import React, {useEffect, useRef, useState} from 'react';
import {Box, Link, Typography} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import {Link as RouterLink, useNavigate, useParams} from 'react-router';
import useChatMessagesStore from '../../../store/useChatMessagesStore';
import remarkGfm from "remark-gfm";

const _awpBase = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';
// Dedicated endpoint — model selection is entirely a backend concern (openai.api.fintel-chat-model).
const STREAM_ENDPOINT = `${_awpBase}/api/llm/streaming/fintel`;
const SEARCH_ENDPOINT = `${_awpBase}/api/segments/search/ai`;
function _authH() { const t = localStorage.getItem('access_token'); return t ? {'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json'} : {'Content-Type': 'application/json'}; }

// Map stored message tuples [role, text] → LlmMessage {role, content}.
// Our store uses role='system' for AI replies; the LLM API expects 'assistant'.
function toApiMessages(tuples) {
    return tuples.map(([role, text]) => ({
        role: role === 'system' ? 'assistant' : role,
        content: String(text || ''),
    })).filter(m => m.content.trim().length > 0);
}

// Product types that correspond to FINTEL-style analyst intelligence products.
// These are the values stored in reports.meta_json.productType.value — NOT the
// UI label ("FINTEL"). The string "fintel" is never stored in the DB.
const FINTEL_PRODUCT_TYPES = ['analytic', 'tactical_sitrep', 'strategic_sitrep'];

// Search for relevant FINTEL report segments using the user's question.
// Passes filters.productTypes so the backend queries report_segments rather than
// the corpus source-documents table.
// Returns {contextBlock, topDocId} or null if nothing useful is found.
async function findRelevantFintel(userQuestion, signal) {
    try {
        const resp = await fetch(SEARCH_ENDPOINT, {
            method: 'POST',
            headers: _authH(),
            body: JSON.stringify({
                query: userQuestion,
                size: 5,
                filters: {productTypes: FINTEL_PRODUCT_TYPES},
            }),
            signal,
        });
        if (!resp.ok) return null;
        const data = await resp.json();
        const segments = (data.data || []).slice(0, 3).filter(s => s.content?.trim());
        if (segments.length === 0) return null;

        const topDocId = segments[0]?.docId ?? null;
        const contextBlock = segments
            .map((s, i) => `[${i + 1}] ${s.docTitle || 'Unknown Report'}\n${s.content.trim()}`)
            .join('\n\n---\n\n');
        return {contextBlock, topDocId, segments};
    } catch {
        return null;
    }
}

async function patchMessage(chatId, msgId, patch) {
    if (!chatId || !msgId) return;
    await fetch(`${_awpBase}/api/chats/${encodeURIComponent(chatId)}/messages/${encodeURIComponent(msgId)}`, {
        method: 'PATCH', headers: _authH(), body: JSON.stringify(patch),
    }).catch(err => console.error('patchMessage error', err));
}

const SystemMessageMarkdown = ({message}) => {
    const {chatId, sourceSegmentId} = useParams();
    const navigate = useNavigate();
    const upsertMessage = useChatMessagesStore(s => s.upsertMessage);

    // Get all messages for this chat so we can send the last 3
    const allMessages = useChatMessagesStore(s => (chatId ? (s.messagesByChat[chatId] || []) : []));

    const [markdown, setMarkdown] = useState('');
    const [error, setError] = useState(null);
    const [sources, setSources] = useState([]);
    const startedRef = useRef(false);

    useEffect(() => {
        // If message already has text or is marked complete, just show it and do not fetch
        if ((message?.status === 'complete') || (message?.text && String(message.text).length > 0)) {
            setMarkdown(message.text);
            // Restore persisted sources so "Referenced in:" and inline citations render from history
            if (Array.isArray(message.sources) && message.sources.length > 0) {
                setSources(message.sources);
            }
            return;
        }

        if (startedRef.current) return; // Avoid duplicate fetches for the same message instance

        // Build conversation history: last 6 messages (3 pairs), excluding this pending reply
        const previousMessages = (allMessages || []).slice(0, -1);
        const apiMessages = toApiMessages(
            previousMessages.slice(-6).map(m => [m.role, m.text])
        );
        if (apiMessages.length === 0) return;

        startedRef.current = true;
        setError(null);
        setMarkdown('');

        const controller = new AbortController();
        const {signal} = controller;

        async function saveError(errorText) {
            if (chatId && message?.id) {
                upsertMessage(chatId, {id: message.id, role: 'system', text: errorText, status: 'error', pending: false});
                await patchMessage(chatId, message.id, {status: 'error', text: errorText});
            }
        }

        (async () => {
            try {
                // ── Step 1: RAG — find the most relevant FINTEL for this question ──
                const userQuestion = previousMessages.slice().reverse().find(m => m.role === 'user')?.text;
                let ragMessages = [...apiMessages];
                let savedSources = [];

                if (userQuestion) {
                    const fintel = await findRelevantFintel(userQuestion, signal);
                    if (fintel) {
                        const {contextBlock, topDocId, segments} = fintel;
                        savedSources = segments;
                        setSources(segments);
                        // Prepend system context so the model answers from FINTEL content
                        ragMessages = [
                            {
                                role: 'system',
                                content:
'You are an expert intelligence analyst assistant. ' +
'Always respond in well-structured Markdown: use headings, bullet points, and bold text where appropriate. ' +
'Answer the user\'s question using ONLY the numbered FINTEL excerpts provided below. ' +
'Cite sources using [1], [2], [3] notation inline whenever you reference a specific excerpt. ' +
'If the excerpts do not contain enough information to answer, say so clearly.\n\n' +
'FINTEL excerpts:\n\n' + contextBlock,
                            },
                            ...ragMessages,
                        ];

                        // TODO: navigate right panel to topDocId once ProductPreviewSection
                        // is migrated away from Firestore IDs to PostgreSQL docIds.
                        // Navigating here would change route params mid-stream, triggering
                        // the useEffect cleanup and aborting the SSE connection.
                    }
                }

                // ── Step 2: stream the LLM response ──
                const res = await fetch(STREAM_ENDPOINT, {
                    method: 'POST',
                    headers: _authH(),
                    body: JSON.stringify({messages: ragMessages}),
                    signal,
                });

                if (!res.ok) {
                    const errorText = 'Sorry, something went wrong. Please try again later.';
                    setError(new Error(`Streaming request failed: ${res.status}`));
                    setMarkdown(errorText);
                    await saveError(errorText);
                    return;
                }

                // Parse SSE stream: events have the form
                //   event: message\ndata: {"content":"..."}\n\n
                //   event: done\ndata: {...}\n\n
                //   event: error\ndata: {"error":"..."}\n\n
                const reader = res.body.getReader();
                const decoder = new TextDecoder('utf-8');
                let rawBuf = '';
                let answer = '';
                let done = false;

                while (!done) {
                    const {value, done: doneReading} = await reader.read();
                    done = doneReading;
                    if (value) rawBuf += decoder.decode(value, {stream: !doneReading});

                    // Process complete SSE blocks (separated by \n\n)
                    const blocks = rawBuf.split('\n\n');
                    rawBuf = blocks.pop() ?? ''; // keep incomplete trailing block

                    for (const block of blocks) {
                        let eventName = '';
                        const dataLines = [];
                        for (const line of block.split('\n')) {
                            if (line.startsWith('event:')) {
                                eventName = line.slice(6).trim();
                            } else if (line.startsWith('data:')) {
                                dataLines.push(line.slice(5));
                            }
                        }
                        // Per SSE spec: multiple data: lines in one block are joined with \n
                        const dataLine = dataLines.join('\n');
                        if (eventName === 'message' && dataLines.length > 0) {
                            answer += dataLine;
                            setMarkdown(answer);
                        } else if (eventName === 'error' && dataLine) {
                            const parsed = JSON.parse(dataLine).error || 'Unknown error';
                            throw new Error(parsed);
                        }
                        // 'done' event signals end of stream — loop exits naturally
                    }
                }

                // If the stream closed with zero content, treat it as an error so the
                // message is not silently marked complete with an empty bubble.
                if (!answer.trim()) {
                    const errorText = 'Sorry, the AI did not return a response. Please try again.';
                    setMarkdown(errorText);
                    await saveError(errorText);
                    return;
                }
                if (chatId && message?.id) {
                    upsertMessage(chatId, {id: message.id, role: 'system', text: answer, status: 'complete', pending: false, sources: savedSources});
                    await patchMessage(chatId, message.id, {status: 'complete', text: answer, sources: savedSources});
                }

            } catch (err) {
                if (signal.aborted) return;
                console.error('SystemMessageMarkdown stream error', err);
                setError(err);
                const errorText = 'Sorry, something went wrong. Please try again later.';
                await saveError(errorText);
                setMarkdown(errorText);
            }
        })();

        return () => controller.abort();
    }, [allMessages, chatId, message?.id, message?.status, message?.text, upsertMessage]);

    return (
        <Box>
            {error && (
                <Typography variant="caption" color="error" sx={{display: 'block', mb: 1}}>
                    Failed to load response: {String(error?.message || error)}
                </Typography>
            )}
            {!!markdown &&
                <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                        h1: ({node, ...props}) => (
                            <Typography variant="h5" fontWeight={600} sx={{mt: 2, mb: 1}} {...props} />
                        ),
                        h2: ({node, ...props}) => (
                            <Typography variant="h6" fontWeight={600} sx={{mt: 2, mb: 1}} {...props} />
                        ),
                        h3: ({node, ...props}) => (
                            <Typography variant="h6" fontWeight={600} sx={{mt: 2, mb: 1}} {...props} />
                        ),
                        h4: ({node, ...props}) => (
                            <Typography variant="h6" fontWeight={600} sx={{mt: 2, mb: 1}}{...props} />
                        ),
                        h5: ({node, ...props}) => (
                            <Typography variant="h6" fontWeight={600} sx={{mt: 2, mb: 1}} {...props} />
                        ),
                        h6: ({node, ...props}) => (
                            <Typography variant="h6" fontWeight={600} sx={{mt: 2, mb: 1}} {...props} />
                        ),
                        a: ({node, ...props}) => {
                            const {href, children} = props;
                            if (href?.startsWith('#s')) {
                                const idx = parseInt(href.slice(2), 10) - 1;
                                return (
                                    <Box
                                        component="span"
                                        onClick={() => {
                                            const src = sources[idx];
                                            if (src?.reportId && chatId) {
                                                const q = encodeURIComponent((src.content || '').trim().slice(0, 80));
                                                navigate(`/chat/fintel/${chatId}/${src.reportId}${q ? `?q=${q}` : ''}`);
                                            }
                                        }}
                                        sx={{
                                            color: 'primary.main',
                                            cursor: 'pointer',
                                            fontWeight: 700,
                                            fontSize: '0.75em',
                                            verticalAlign: 'super',
                                            px: 0.3,
                                            py: 0.1,
                                            borderRadius: '3px',
                                            bgcolor: 'action.hover',
                                            '&:hover': {bgcolor: 'primary.light', color: 'primary.contrastText'},
                                        }}
                                    >
                                        {children}
                                    </Box>
                                );
                            }
                            return <Link
                                sx={{bgcolor: sourceSegmentId === href ? 'yellow' : null}}
                                component={RouterLink}
                                to={{pathname: chatId + "/" + href}}
                                children={children}
                            />;
                        },
                        p: ({node, ...props}) => (
                            <Typography variant="body1" sx={{mb: 1}} {...props} />
                        ),
                        ul: ({node, ...props}) => (
                            <Box component="ul" sx={{pl: 2, mb: 1}} {...props} />
                        ),
                        ol: ({node, ...props}) => (
                            <Box component="ol" sx={{pl: 2, mb: 1}} {...props} />
                        ),
                        li: ({node, ...props}) => (
                            <Typography component="li" variant="body1" {...props} />
                        ),
                        code: ({node, inline, className, children, ...props}) => {
                            if (inline) {
                                return (
                                    <Box component="code" sx={{
                                        backgroundColor: 'rgba(0,0,0,0.06)',
                                        px: 0.5,
                                        borderRadius: '4px'
                                    }} {...props}>
                                        {children}
                                    </Box>
                                );
                            }
                            return (
                                <Box component="pre"
                                     sx={{backgroundColor: '#f6f8fa', p: 1, borderRadius: 1, overflow: 'auto'}}>
                                    <Box component="code" className={className} {...props}>{children}</Box>
                                </Box>
                            );
                        }
                    }}
                >
                    {markdown.replace(/\[(\d+)\]/g, (match, n) => {
                        const idx = parseInt(n, 10);
                        return (idx >= 1 && idx <= sources.length) ? `[[${n}]](#s${n})` : match;
                    })}
                </ReactMarkdown>}
            {sources.length > 0 && (() => {
                // Deduplicate by reportId; keep insertion order
                const seen = new Set();
                const unique = sources.filter(s => {
                    if (!s.reportId || seen.has(s.reportId)) return false;
                    seen.add(s.reportId);
                    return true;
                });
                return (
                    <Box sx={{mt: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5}}>
                        <Typography variant="caption" color="text.disabled" sx={{mr: 0.5}}>
                            Referenced in:
                        </Typography>
                        {unique.map((s, i) => (
                            <Typography
                                key={s.reportId}
                                component="span"
                                variant="caption"
                                onClick={() => {
                                    if (s.reportId && chatId) {
                                        const q = encodeURIComponent((s.content || '').trim().slice(0, 80));
                                        navigate(`/chat/fintel/${chatId}/${s.reportId}${q ? `?q=${q}` : ''}`);
                                    }
                                }}
                                sx={{
                                    color: 'primary.main',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    textDecorationStyle: 'dotted',
                                    '&:hover': {textDecorationStyle: 'solid'},
                                }}
                            >
                                {s.docTitle || 'Unknown Report'}{i < unique.length - 1 ? ' ·' : ''}
                            </Typography>
                        ))}
                    </Box>
                );
            })()}
        </Box>
    );
};

export default SystemMessageMarkdown;
